"use client";

import { useMemo } from "react";
import {
  ApolloProvider as Provider,
  ApolloClient,
  createHttpLink,
  from,
  InMemoryCache,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";

function createApolloClient() {
  const httpLink = createHttpLink({
    uri:
      import.meta.env.VITE_APOLLO_CLIENT_URI ||
      "https://api-staging.withsplice.com/graphql",
  });

  const errorLink = onError(
    ({ graphQLErrors, networkError, operation, forward }) => {
      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path, extensions }) => {
          console.error(
            `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
          );

          // Handle authentication errors
          if (
            extensions?.code === "UNAUTHENTICATED" ||
            message.includes("Unauthorized")
          ) {
            console.warn("Authentication error detected, clearing token");
            localStorage.removeItem("customer-token");
            // Optionally redirect to login page
            if (typeof window !== "undefined") {
              window.location.href = "/login";
            }
          }
        });
      }
      if (networkError) {
        console.error(`[Network error]: ${networkError}`);

        // Handle network errors that might be auth-related
        if (
          networkError.message.includes("401") ||
          networkError.message.includes("Unauthorized")
        ) {
          console.warn("Network authentication error detected, clearing token");
          localStorage.removeItem("customer-token");
        }
      }
    }
  );

  const authLink = setContext((_, { headers }) => {
    // Get token from localStorage only in browser
    const token =
      typeof window !== "undefined"
        ? localStorage.getItem("customer-token")
        : null;

    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : "",
      },
    };
  });

  const cache = new InMemoryCache({
    typePolicies: {
      CWBaseRecord: {
        keyFields: false,
      },
    },
  });

  // Combine Links
  const link = from([authLink, errorLink, httpLink]);

  return new ApolloClient({
    link,
    cache,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-and-network",
      },
    },
  });
}

export function ApolloProvider({ children }: { children: React.ReactNode }) {
  const client = useMemo(() => createApolloClient(), []);

  return <Provider client={client}>{children}</Provider>;
}
